import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { CurrencyTotal, ReceiptListItem, receipts } from "../api/client";
import MoneyTotals, { addTotals } from "../components/MoneyTotals";
import { formatReceiptDate } from "../receiptDate";

function formatCents(cents: number | null | undefined, currency: string | null | undefined): string {
  if (cents == null) return "—";
  const value = cents / 100;
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: currency || "USD",
    }).format(value);
  } catch {
    return `$${value.toFixed(2)}`;
  }
}

export default function CardDetail() {
  const { last4 = "" } = useParams<{ last4: string }>();
  const [items, setItems] = useState<ReceiptListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [spent, setSpent] = useState<CurrencyTotal[] | null>(null);

  useEffect(() => {
    receipts
      .list({ card_last4: last4, limit: 1000 })
      .then((rows) => setItems(rows))
      .catch((e) => setError(e instanceof Error ? e.message : "Could not load"))
      .finally(() => setLoading(false));
    // Server-side totals, split by currency (every brand listed under these digits).
    receipts
      .listCards()
      .then((cards) => setSpent(addTotals(cards.filter((c) => c.card_last4 === last4).map((c) => c.totals))))
      .catch(() => setSpent(null));
  }, [last4]);

  const brand = items.find((r) => r.card_brand)?.card_brand ?? "Card";
  const title = `${brand} ···· ${last4}`;

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <Link to="/app/cards" className="muted">← Cards</Link>
          <h1 style={{ marginTop: 6 }}>{title}</h1>
        </div>
      </div>
      {spent && spent.length > 0 && (
        <div className="card-spend">
          <span className="muted">Total spent</span>
          <MoneyTotals totals={spent} />
        </div>
      )}
      {loading ? (
        <div className="page-empty">Loading…</div>
      ) : error ? (
        <div className="page-error">{error}</div>
      ) : items.length === 0 ? (
        <div className="page-empty">No receipts found for this card.</div>
      ) : (
        <table className="data-table">
          <thead>
            <tr>
              <th>Date</th>
              <th>Vendor</th>
              <th>Subject</th>
              <th style={{ textAlign: "right" }}>Amount</th>
            </tr>
          </thead>
          <tbody>
            {items.map((r) => (
              <tr key={r.id}>
                <td>{formatReceiptDate(r)}</td>
                <td>
                  <Link to={`/app/receipts/${r.id}`}>
                    {r.vendor_name || r.sender || "Unknown"}
                  </Link>
                </td>
                <td>{r.subject || "(no subject)"}</td>
                <td style={{ textAlign: "right" }}>
                  {formatCents(r.amount_cents, r.currency)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
